// MP3TagProcessors.js
// Fonctions pures pour LECTURE ID3 (v1/v2) et ÉCRITURE (construction de tag + fusion MP3)

/* ----------------------- Utils de nettoyage ----------------------- */
export const cleanNulls = (s) => (s ?? '').replace(/\u0000/g, '');
export const digitsOnly = (s) => cleanNulls(s).replace(/[^\d]/g, '');
export const finalizeText = (s) => cleanNulls(s).replace(/\s+/g, ' ').trim();

/* ----------------------- Helpers binaires ----------------------- */
const readSynchsafe32 = (dv, off) =>
  ((dv.getUint8(off) & 0x7f) << 21) |
  ((dv.getUint8(off + 1) & 0x7f) << 14) |
  ((dv.getUint8(off + 2) & 0x7f) << 7) |
  (dv.getUint8(off + 3) & 0x7f);

const decodeText = (bytes, encodingByte = 0) => {
  if (!bytes || bytes.length === 0) return '';
  try {
    if (encodingByte === 0) return new TextDecoder('iso-8859-1').decode(bytes);
    if (encodingByte === 3) return new TextDecoder('utf-8').decode(bytes);
    if (encodingByte === 1) return new TextDecoder('utf-16').decode(bytes);   // with BOM
    if (encodingByte === 2) return new TextDecoder('utf-16be').decode(bytes); // no BOM
  } catch {}
  return new TextDecoder().decode(bytes);
};

const findTerminator = (u8, start, encodingByte) => {
  if (encodingByte === 1 || encodingByte === 2) {
    for (let i = start; i + 1 < u8.length; i += 2) {
      if (u8[i] === 0x00 && u8[i + 1] === 0x00) return i;
    }
    return -1;
  } else {
    for (let i = start; i < u8.length; i++) if (u8[i] === 0x00) return i;
    return -1;
  }
};

const pictureToDataURL = (mime, bin) => {
  let binary = '';
  for (let i = 0; i < bin.length; i++) binary += String.fromCharCode(bin[i]);
  return `data:${mime || 'image/jpeg'};base64,${btoa(binary)}`;
};

// Retire l'unsynchronisation (0xFF 0x00 -> 0xFF) sur un buffer
const removeUnsync = (u8) => {
  const out = [];
  for (let i = 0; i < u8.length; i++) {
    out.push(u8[i]);
    if (u8[i] === 0xFF && i + 1 < u8.length && u8[i + 1] === 0x00) {
      i++; // saute 0x00
    }
  }
  return new Uint8Array(out);
};

// Lecture de l'en-tête étendu (retourne l'offset après en-tête étendu)
const skipExtendedHeader = (u8, versionMajor, start) => {
  if (versionMajor === 3) {
    if (start + 4 > u8.length) return start;
    const dv = new DataView(u8.buffer, u8.byteOffset, u8.byteLength);
    const size = dv.getUint32(start); // big-endian
    return Math.min(u8.length, start + 4 + size);
  } else if (versionMajor === 4) {
    if (start + 4 > u8.length) return start;
    const dv = new DataView(u8.buffer, u8.byteOffset, u8.byteLength);
    const size = readSynchsafe32(dv, start);
    return Math.min(u8.length, start + 4 + size);
  }
  return start;
};

/* ----------------------- Lecture ID3v2 (v2.2/2.3/2.4) ----------------------- */
const parseID3v2 = async (file) => {
  const head = new Uint8Array(await file.slice(0, 10).arrayBuffer());
  if (head.length < 10) return null;
  if (head[0] !== 0x49 || head[1] !== 0x44 || head[2] !== 0x33) return null;

  const versionMajor = head[3];
  const flags = head[5];
  const dvh = new DataView(head.buffer);
  const tagSize = readSynchsafe32(dvh, 6);
  const hasFooter = versionMajor === 4 ? ((flags & 0x10) !== 0) : false;
  const totalToRead = 10 + tagSize + (hasFooter ? 10 : 0);
  const tagBuf = new Uint8Array(await file.slice(0, totalToRead).arrayBuffer());
  if (tagBuf.length < 10) return null;

  const unsyncGlobal = (flags & 0x80) !== 0;
  let body = tagBuf.slice(10, 10 + tagSize);
  if (unsyncGlobal) body = removeUnsync(body);

  let offset = 0;
  const hasExtHeader = versionMajor === 3 ? ((flags & 0x40) !== 0)
                                          : (versionMajor === 4 ? ((flags & 0x40) !== 0) : false);
  if (hasExtHeader) {
    const start = offset;
    offset = skipExtendedHeader(body, versionMajor, offset);
    if (offset <= start) offset = start;
  }

  const tags = {
    title: '', artist: '', album: '', albumArtist: '',
    year: '', track: '', genre: '', comment: '', lyrics: '',
    encoder: '', composer: '', bpm: '', key: '', language: '',
    copyright: '', totalTracks: '', disc: '', totalDiscs: ''
  };
  let cover = null;
  let preferredCoverFound = false;

  const readFrameSize = (u8, off) => {
    const dv = new DataView(u8.buffer, u8.byteOffset, u8.byteLength);
    if (versionMajor === 4) return readSynchsafe32(dv, off + 4);
    if (versionMajor === 3) return dv.getUint32(off + 4);
    return (u8[off + 3] << 16) | (u8[off + 4] << 8) | (u8[off + 5]); // v2.2
  };

  while (true) {
    if (versionMajor === 2) {
      if (offset + 6 > body.length) break;
      if (body[offset] === 0x00) break;
      const frameId = new TextDecoder('ascii').decode(body.slice(offset, offset + 3));
      if (!/^[A-Z0-9]{3}$/.test(frameId)) break;
      const size = readFrameSize(body, offset);
      const frameStart = offset + 6;
      const frameEnd = frameStart + size;
      if (frameEnd > body.length || size <= 0) break;

      const frameData = body.slice(frameStart, frameEnd);

      if (frameId === 'PIC') {
        const enc = frameData[0] || 0;
        const imgFmt = new TextDecoder('ascii').decode(frameData.slice(1, 4)) || 'JPG';
        const picType = frameData[4] || 3;
        let descStart = 5;
        const term = findTerminator(frameData, descStart, enc);
        let imgStart = descStart;
        if (term !== -1) imgStart = (enc === 1 || enc === 2) ? term + 2 : term + 1;
        const imgBytes = frameData.slice(imgStart);
        const mime = (imgFmt.toUpperCase() === 'PNG') ? 'image/png' : 'image/jpeg';
        if (!cover || (!preferredCoverFound && picType === 3)) {
          cover = pictureToDataURL(mime, imgBytes);
          if (picType === 3) preferredCoverFound = true;
        }
      } else if (/^T[A-Z0-9]{2}$/.test(frameId) && size > 0) {
        const enc = frameData[0] || 0;
        const txt = decodeText(frameData.slice(1), enc).trim();
        if (frameId === 'TT2') tags.title = cleanNulls(txt);
        else if (frameId === 'TP1') tags.artist = cleanNulls(txt);
        else if (frameId === 'TAL') tags.album = cleanNulls(txt);
        else if (frameId === 'TP2') tags.albumArtist = cleanNulls(txt);
        else if (frameId === 'TRK') {
          const t = cleanNulls(txt);
          if (t.includes('/')) { const [a,b] = t.split('/'); tags.track = a; tags.totalTracks = b; } else tags.track = t;
        }
        else if (frameId === 'TPA') {
          const t = cleanNulls(txt);
          if (t.includes('/')) { const [a,b] = t.split('/'); tags.disc = a; tags.totalDiscs = b; } else tags.disc = t;
        }
        else if (frameId === 'TCO') tags.genre = cleanNulls(txt);
        else if (frameId === 'TYE') tags.year = digitsOnly(txt);
        else if (frameId === 'TCM') tags.composer = cleanNulls(txt);
        else if (frameId === 'TEN') tags.encoder = cleanNulls(txt);
      } else if (frameId === 'COM' && size > 0) {
        const enc = frameData[0] || 0;
        let cursor = 4;
        const term = findTerminator(frameData, cursor, enc);
        if (term !== -1) cursor = (enc === 1 || enc === 2) ? term + 2 : term + 1;
        const commText = cleanNulls(decodeText(frameData.slice(cursor), enc));
        tags.comment = [tags.comment, commText].filter(Boolean).join(' | ');
      }

      offset = frameEnd;
    } else {
      if (offset + 10 > body.length) break;
      if (body[offset] === 0x00) break;
      const frameId = new TextDecoder('ascii').decode(body.slice(offset, offset + 4));
      if (!/^[A-Z0-9]{4}$/.test(frameId)) break;

      const size = readFrameSize(body, offset);
      const flags2 = body[offset + 9] || 0;
      let frameStart = offset + 10;
      let frameEnd = frameStart + size;
      if (frameEnd > body.length || size <= 0) break;

      let frameData = body.slice(frameStart, frameEnd);

      const frameUnsync = (versionMajor === 4) && ((flags2 & 0x02) !== 0);
      if (frameUnsync) frameData = removeUnsync(frameData);

      if (/^T[A-Z0-9]{3}$/.test(frameId) && frameId !== 'TXXX') {
        const enc = frameData[0] || 0;
        const text = decodeText(frameData.slice(1), enc).trim();
        if (frameId === 'TIT2') tags.title = cleanNulls(text);
        else if (frameId === 'TPE1') tags.artist = cleanNulls(text);
        else if (frameId === 'TALB') tags.album = cleanNulls(text);
        else if (frameId === 'TPE2') tags.albumArtist = cleanNulls(text);
        else if (frameId === 'TRCK') {
          const t = cleanNulls(text);
          if (t.includes('/')) { const [a,b] = t.split('/'); tags.track = a; tags.totalTracks = b; } else tags.track = t;
        }
        else if (frameId === 'TPOS') {
          const t = cleanNulls(text);
          if (t.includes('/')) { const [a,b] = t.split('/'); tags.disc = a; tags.totalDiscs = b; } else tags.disc = t;
        }
        else if (frameId === 'TCON') tags.genre = cleanNulls(text);
        else if (frameId === 'TYER' || frameId === 'TDRC') tags.year = digitsOnly(text);
        else if (frameId === 'TCOM') tags.composer = cleanNulls(text);
        else if (frameId === 'TSSE') tags.encoder = cleanNulls(text);
        else if (frameId === 'TBPM') tags.bpm = digitsOnly(text);
        else if (frameId === 'TKEY') tags.key = cleanNulls(text);
        else if (frameId === 'TLAN') tags.language = cleanNulls(text);
        else if (frameId === 'TCOP') tags.copyright = cleanNulls(text);
      } else if (frameId === 'COMM' && frameData.length > 4) {
        const enc = frameData[0] || 0;
        let cursor = 4;
        const term = findTerminator(frameData, cursor, enc);
        if (term !== -1) cursor = (enc === 1 || enc === 2) ? term + 2 : term + 1;
        const commText = cleanNulls(decodeText(frameData.slice(cursor), enc));
        tags.comment = [tags.comment, commText].filter(Boolean).join(' | ');
      } else if (frameId === 'USLT' && frameData.length > 4) {
        const enc = frameData[0] || 0;
        let cursor = 4;
        const term = findTerminator(frameData, cursor, enc);
        if (term !== -1) cursor = (enc === 1 || enc === 2) ? term + 2 : term + 1;
        tags.lyrics = cleanNulls(decodeText(frameData.slice(cursor), enc));
      } else if (frameId === 'APIC' && frameData.length > 4) {
        const enc = frameData[0] || 0;
        let i = 1;
        for (; i < frameData.length && frameData[i] !== 0x00; i++);
        const mime = new TextDecoder('ascii').decode(frameData.slice(1, i)) || 'image/jpeg';
        const picType = frameData[i + 1] || 3;
        let descStart = i + 2;
        const descTerm = findTerminator(frameData, descStart, enc);
        let imgStart = descStart;
        if (descTerm !== -1) imgStart = (enc === 1 || enc === 2) ? descTerm + 2 : descTerm + 1;
        const imgBytes = frameData.slice(imgStart);
        if (!cover || (!preferredCoverFound && picType === 3)) {
          cover = pictureToDataURL(mime, imgBytes);
          if (picType === 3) preferredCoverFound = true;
        }
      }

      offset = frameEnd;
    }
  }

  return { version: `2.${versionMajor}`, size: tagSize, tags, coverArt: cover };
};

/* ----------------------- Lecture ID3v1 ----------------------- */
const parseID3v1 = async (file) => {
  if (file.size < 128) return null;
  const tail = await file.slice(file.size - 128, file.size).arrayBuffer();
  const u8 = new Uint8Array(tail);
  if (u8[0] !== 0x54 || u8[1] !== 0x41 || u8[2] !== 0x47) return null;
  const dec = (start, len) =>
    new TextDecoder('iso-8859-1')
      .decode(u8.slice(start, start + len))
      .replace(/\0+$/, '')
      .trim();
  const title = dec(3, 30);
  const artist = dec(33, 30);
  const album = dec(63, 30);
  const year = dec(93, 4);
  const comment = dec(97, 30);
  const genreByte = u8[127];
  return {
    version: '1.0',
    tags: { title, artist, album, year, comment, genre: String(genreByte) },
    coverArt: null
  };
};

/* ----------------------- API PUBLIQUES : Lecture ----------------------- */
export const readAudioTags = async (file) => {
  const v2 = await parseID3v2(file);
  if (v2) return v2;
  const v1 = await parseID3v1(file);
  if (v1) return v1;
  return { version: null, tags: {}, coverArt: null };
};

/* Fallback: créer des tags basiques à partir du nom de fichier */
export const fallbackTagsFromFilename = (file, defaultTags = {}) => {
  const fileName = file.name.replace(/\.[^/.]+$/, '');
  let title = fileName;
  let artist = '';
  if (fileName.includes(' - ')) {
    const parts = fileName.split(' - ');
    artist = parts[0].trim();
    title = parts.slice(1).join(' - ').trim();
  }
  const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
  return {
    ...defaultTags,
    title: title || fileName,
    artist: artist || '',
    albumArtist: artist || '',
    comment: `Taille: ${fileSizeMB} MB - Édité avec MP3 Tag Editor Pro`
  };
};

/* ----------------------- API PUBLIQUES : Écriture ----------------------- */
export const createSimpleID3Tag = (tags, coverDataURL) => {
  const frames = [];
  const encByte = 0; // ISO-8859-1

  const createTextFrame = (id, text) => {
    if (!text || !finalizeText(text)) return null;
    const textBytes = new TextEncoder().encode(finalizeText(text));
    const frame = new Uint8Array(10 + 1 + textBytes.length);
    frame.set(new TextEncoder().encode(id), 0);
    const size = 1 + textBytes.length;
    frame[4] = (size >>> 24) & 0xFF;
    frame[5] = (size >>> 16) & 0xFF;
    frame[6] = (size >>> 8) & 0xFF;
    frame[7] = size & 0xFF;
    frame[8] = 0; frame[9] = 0;
    frame[10] = encByte;
    frame.set(textBytes, 11);
    return frame;
  };

  const frameData = [
    ['TIT2', tags.title],
    ['TPE1', tags.artist],
    ['TALB', tags.album],
    ['TPE2', tags.albumArtist],
    ['TYER', tags.year],
    ['TRCK', tags.track + (tags.totalTracks ? `/${tags.totalTracks}` : '')],
    ['TPOS', tags.disc + (tags.totalDiscs ? `/${tags.totalDiscs}` : '')],
    ['TCON', tags.genre],
    ['TCOM', tags.composer],
    ['TBPM', tags.bpm],
    ['TKEY', tags.key],
    ['TLAN', tags.language],
    ['TCOP', tags.copyright],
    ['TSSE', tags.encoder],
  ];
  frameData.forEach(([id, value]) => {
    const frame = createTextFrame(id, value);
    if (frame) frames.push(frame);
  });

  // COMM
  if (tags.comment && finalizeText(tags.comment)) {
    const commentBytes = new TextEncoder().encode(finalizeText(tags.comment));
    const size = 1 + 3 + 1 + 1 + commentBytes.length;
    const frame = new Uint8Array(10 + size);
    frame.set(new TextEncoder().encode('COMM'), 0);
    frame[4] = (size >>> 24) & 0xFF;
    frame[5] = (size >>> 16) & 0xFF;
    frame[6] = (size >>> 8) & 0xFF;
    frame[7] = size & 0xFF;
    frame[8] = 0; frame[9] = 0;
    let o = 10;
    frame[o++] = encByte;
    frame.set(new TextEncoder().encode('eng'), o); o += 3;
    frame[o++] = 0x00; // short desc ""
    frame[o++] = 0x00; // terminator
    frame.set(commentBytes, o);
    frames.push(frame);
  }

  // USLT (lyrics)
  if (tags.lyrics && finalizeText(tags.lyrics)) {
    const lyricsBytes = new TextEncoder().encode(finalizeText(tags.lyrics));
    const size = 1 + 3 + 1 + lyricsBytes.length;
    const frame = new Uint8Array(10 + size);
    frame.set(new TextEncoder().encode('USLT'), 0);
    frame[4] = (size >>> 24) & 0xFF;
    frame[5] = (size >>> 16) & 0xFF;
    frame[6] = (size >>> 8) & 0xFF;
    frame[7] = size & 0xFF;
    frame[8] = 0; frame[9] = 0;
    let o = 10;
    frame[o++] = encByte;
    frame.set(new TextEncoder().encode('eng'), o); o += 3;
    frame[o++] = 0x00; // desc ""
    frame.set(lyricsBytes, o);
    frames.push(frame);
  }

  // APIC (cover)
  if (coverDataURL) {
    try {
      const dataURLToBytes = (dataURL) => {
        const [header, base64] = dataURL.split(',');
        const mime = header.match(/data:(.*);base64/)[1] || 'image/jpeg';
        const bin = atob(base64);
        const bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
        return { mime, bytes };
      };
      const { mime, bytes } = dataURLToBytes(coverDataURL);
      const mimeBytes = new TextEncoder().encode(mime);
      const size = 1 + mimeBytes.length + 1 + 1 + 1 + bytes.length;
      const frame = new Uint8Array(10 + size);
      frame.set(new TextEncoder().encode('APIC'), 0);
      frame[4] = (size >>> 24) & 0xFF;
      frame[5] = (size >>> 16) & 0xFF;
      frame[6] = (size >>> 8) & 0xFF;
      frame[7] = size & 0xFF;
      frame[8] = 0; frame[9] = 0;
      let o = 10;
      frame[o++] = encByte;
      frame.set(mimeBytes, o); o += mimeBytes.length;
      frame[o++] = 0x00;        // terminator MIME
      frame[o++] = 3;           // type: front cover
      frame[o++] = 0x00;        // desc "" + terminator
      frame.set(bytes, o);
      frames.push(frame);
    } catch (e) {
      console.warn('Erreur ajout pochette:', e);
    }
  }

  // Assembler tag ID3v2.3
  let totalSize = 0;
  frames.forEach(f => totalSize += f.length);

  const header = new Uint8Array(10);
  header.set(new TextEncoder().encode('ID3'), 0);
  header[3] = 3; // v2.3.0
  header[4] = 0;
  header[5] = 0;
  // synchsafe
  header[6] = (totalSize >>> 21) & 0x7F;
  header[7] = (totalSize >>> 14) & 0x7F;
  header[8] = (totalSize >>> 7) & 0x7F;
  header[9] = totalSize & 0x7F;

  const tag = new Uint8Array(10 + totalSize);
  tag.set(header, 0);
  let o = 10;
  frames.forEach(f => { tag.set(f, o); o += f.length; });

  return tag;
};

/**
 * Fusionne le nouveau tag avec l'audio original (remplace l'ancien tag v2 s'il existe)
 * @returns {Promise<{blob: Blob, suggestedName: string}>}
 */
export const createModifiedMp3Blob = async (originalFile, tags, coverDataURL) => {
  const arrayBuffer = await originalFile.arrayBuffer();
  const originalData = new Uint8Array(arrayBuffer);

  // Calculer début des données audio après éventuel ID3v2 existant
  let audioDataStart = 0;
  if (originalData[0] === 0x49 && originalData[1] === 0x44 && originalData[2] === 0x33) {
    const tagSize = ((originalData[6] & 0x7F) << 21) |
                    ((originalData[7] & 0x7F) << 14) |
                    ((originalData[8] & 0x7F) << 7) |
                    (originalData[9] & 0x7F);
    audioDataStart = 10 + tagSize;
  }

  const newTag = createSimpleID3Tag(tags, coverDataURL || null);
  const audioData = originalData.slice(audioDataStart);
  const modifiedFile = new Uint8Array(newTag.length + audioData.length);
  modifiedFile.set(newTag, 0);
  modifiedFile.set(audioData, newTag.length);

  const suggestedName = `${finalizeText(tags.title || originalFile.name.replace(/\.[^/.]+$/, ''))}.mp3`;
  const blob = new Blob([modifiedFile], { type: 'audio/mpeg' });
  return { blob, suggestedName };
};
